import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  activePartyService,
  type MyBuyersResponse,
  type MyPartyItem,
} from '../../services/api/activePartyService';

/**
 * Loads the signed-in user's buyer list for a tenant. `persistedBuyerId` is the
 * caller's last-selected buyer (from localStorage / preference) — echoed back so
 * the slice can run the resolution order on `fulfilled` without re-reading
 * storage. Spec: active-party-top-bar.
 */
export const fetchMyBuyers = createAsyncThunk<
  { response: MyBuyersResponse; persistedBuyerId: number | null },
  {
    tenantId: number;
    searchText?: string;
    pageIndex?: number;
    pageSize?: number;
    persistedBuyerId?: number | null;
  }
>('activeParty/fetchMyBuyers', async (arg, { rejectWithValue }) => {
  try {
    const response = await activePartyService.getMyBuyers(arg.tenantId, {
      searchText: arg.searchText,
      pageIndex: arg.pageIndex,
      pageSize: arg.pageSize,
    });
    return { response, persistedBuyerId: arg.persistedBuyerId ?? null };
  } catch (error: unknown) {
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to load buyers');
  }
});

/**
 * Loads the suppliers available for a chosen buyer. `persistedSupplierId` is
 * echoed back so the slice can resolve the active supplier on `fulfilled`.
 */
export const fetchSuppliersForBuyer = createAsyncThunk<
  { buyerPartyId: number; suppliers: MyPartyItem[]; persistedSupplierId: number | null },
  { tenantId: number; buyerPartyId: number; persistedSupplierId?: number | null }
>('activeParty/fetchSuppliersForBuyer', async (arg, { rejectWithValue }) => {
  try {
    const suppliers = await activePartyService.getMySuppliers(arg.tenantId, arg.buyerPartyId);
    return {
      buyerPartyId: arg.buyerPartyId,
      suppliers,
      persistedSupplierId: arg.persistedSupplierId ?? null,
    };
  } catch (error: unknown) {
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to load suppliers');
  }
});
